// components/TermsModal.jsx
import React, { useRef, useState } from "react";
import { HiOutlineX, HiOutlineTrash, HiOutlinePlusCircle } from "react-icons/hi";
import { useDeleteTerm, useGetTermsList, useUploadTerms, fileToBase64Payload }from "../hooks/terms";


const fileNameFromPath = (p = "") => p.split("/").pop() || "file";
const isPreviewable = (name = "") => /\.(pdf|png|jpe?g|gif|webp|svg)$/i.test(name);
const baseUrl = process.env.REACT_APP_BASE_URL + "/terms/" || "";

export default function TermsModal({ open, onClose, formTypeId }) {
  const { data: terms = [], isFetching, isError } = useGetTermsList(formTypeId);
  const { mutateAsync: deleteTerm, isLoading: deleting } = useDeleteTerm();
  const { mutateAsync: uploadTerms, isLoading: uploading } = useUploadTerms();

  const fileRef = useRef(null);
  const [busyMsg, setBusyMsg] = useState("");

  console.log('terms', terms);

  if (!open) return null;

  const handleRemove = async (termId) => {
    if (!window.confirm("Remove this Terms & Conditions file?")) return;
    setBusyMsg("Removing...");
    try {
      console.log('removing',formTypeId, termId );
      await deleteTerm({ formTypeId, termId });
    } finally {
      setBusyMsg("");
    }
  };

  const openPicker = () => fileRef.current?.click();

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;

    if (terms.length > 0) {
      const ok = window.confirm("This will remove the existing Terms & Conditions and upload a new one. Continue?");
      if (!ok) return;

      // Remove ALL existing (one-by-one) before adding — as requested
      setBusyMsg("Clearing existing terms...");
      for (const t of terms) {
        await deleteTerm({ formTypeId, termId: t.termsId });
      }
    }

    setBusyMsg("Uploading new terms...");
    const payload = await fileToBase64Payload(f);
    try {
      await uploadTerms({ formTypeId, payload });
    } finally {
      setBusyMsg("");
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center">
      <div className="bg-white w-[95vw] max-w-3xl rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-lg">Terms & Conditions</h3>
          <button type="button" onClick={onClose} className="rounded p-2 hover:bg-gray-100" aria-label="Close">
            <HiOutlineX size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={openPicker}
              disabled={uploading || deleting}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-60"
              title="Add or Replace"
            >
              <HiOutlinePlusCircle />
              {uploading || busyMsg ? (busyMsg || "Uploading...") : "Add / Replace"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={onPick}
            />
            {isFetching && <span className="text-sm text-gray-500">Loading…</span>}
            {isError && <span className="text-sm text-red-600">Failed to load list</span>}
          </div>

          <ul className="divide-y border rounded">
            {terms.length === 0 && (
              <li className="p-4 text-sm text-gray-500">No Terms & Conditions uploaded yet.</li>
            )}

            {terms.map((t) => {
              const name = t.fileName || fileNameFromPath(t.filePath);
              return (
                <li key={t.termsId} className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{name}</div>
                    <div className="text-xs text-gray-500">
                      {t.activeStatus ? "Active" : "Inactive"}
                      {t.datetimeInserted ? ` • ${new Date(t.datetimeInserted).toLocaleString()}` : null}
                    </div>
                    <div className="mt-1 flex gap-3 text-sm">
                      {/* Open in new tab — works for publicly served static files */}
                      {isPreviewable(name) && (
                        <a
                          href={baseUrl+t.filePath}
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-blue-600"
                        >
                          Open
                        </a>
                      )}
                      <a
                        href={baseUrl+t.filePath}
                        download
                        className="underline text-blue-600"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download
                      </a>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemove(t.termsId)}
                    disabled={deleting || uploading}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-60"
                    title="Remove"
                  >
                    <HiOutlineTrash /> Remove
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
